import { useCallback, useEffect, useState, useRef } from "react";
import type { MessageContextType, MessagePreview, DocumentPublic, ResponseOption, ResponsePresetKey } from "@dugads/shared";
import { RESPONSE_PRESETS } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { X, Send, Users, CheckCircle2, Paperclip, FileText, Upload, Plus, Trash2 } from "lucide-react";

const PRESET_LABELS: Record<string, string> = {
  none: "None (informational)",
  yes_no: "Yes / No",
  attending: "Attending / Not Attending / Maybe",
  arrival: "Arrival Status",
  custom: "Custom",
};

// 178 -- editor-only wrapper: a ResponseOption plus a stable React key id. The
// id is stripped before the option is sent to the API.
interface EditableResponseOption extends ResponseOption {
  id: string;
}

interface SendMessageDialogProps {
  contextType: MessageContextType;
  contextId: string;
  contextLabel: string;
  schoolId?: string;
  defaultPreset?: ResponsePresetKey;
  onClose: () => void;
  onSent?: () => void;
}

export function SendMessageDialog({
  contextType,
  contextId,
  contextLabel,
  schoolId,
  defaultPreset,
  onClose,
  onSent,
}: SendMessageDialogProps) {
  const [content, setContent] = useState("");
  const [groups, setGroups] = useState<string[]>(["parents"]);
  // 163 -- team sub-group target (forwards/backs); null = whole team
  const [subgroup, setSubgroup] = useState<"forwards" | "backs" | null>(null);
  const [responsePreset, setResponsePreset] = useState<string>(defaultPreset ?? "none");
  // 178 -- each custom option carries a stable id so the editable list keys by
  // identity, not array index (index keys mis-route focus/state on delete).
  const [customOptions, setCustomOptions] = useState<EditableResponseOption[]>([
    { id: crypto.randomUUID(), label: "", value: "", color: "green" },
  ]);
  const [preview, setPreview] = useState<MessagePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [attachedDoc, setAttachedDoc] = useState<DocumentPublic | null>(null);
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [schoolDocs, setSchoolDocs] = useState<DocumentPublic[]>([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dragCounter = useRef(0);

  // Resolve school ID: explicit prop > school context > preview response
  const effectiveSchoolId =
    schoolId ?? (contextType === "school" ? contextId : undefined) ?? preview?.school_id;

  function getResponseOptions(): ResponseOption[] | undefined {
    if (responsePreset === "none") return undefined;
    if (responsePreset === "custom") {
      const valid = customOptions
        .filter((o) => o.label.trim() && o.value.trim())
        .map(({ id: _id, ...rest }) => rest);
      return valid.length > 0 ? valid : undefined;
    }
    return [...RESPONSE_PRESETS[responsePreset as ResponsePresetKey]];
  }

  const loadPreview = useCallback(async () => {
    if (groups.length === 0) {
      setPreview(null);
      return;
    }
    setLoadingPreview(true);
    try {
      const res = await api.previewMessage({
        context_type: contextType,
        context_id: contextId,
        groups,
        content: "preview", // content is required by schema but irrelevant for preview
        school_id: schoolId,
        subgroup: contextType === "team" ? subgroup : null,
      });
      setPreview(res.preview);
    } catch (e) {
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }, [contextType, contextId, groups, schoolId, subgroup]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  function toggleGroup(group: string) {
    setGroups((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group],
    );
  }

  function handleOpenDocPicker() {
    if (effectiveSchoolId) {
      api.getSchoolDocuments(effectiveSchoolId).then((res) => setSchoolDocs(res.documents)).catch(() => {});
    }
    setShowDocPicker(true);
  }

  async function handleFileUpload(file: File) {
    if (!effectiveSchoolId) return;
    setUploading(true);
    setError(null);
    try {
      const res = await api.uploadDocument(effectiveSchoolId, file, "general");
      setAttachedDoc(res.document);
      setShowDocPicker(false);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  function handleDragEnter(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes("Files")) {
      setDragging(true);
    }
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragging(false);
    }
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    dragCounter.current = 0;
    const file = e.dataTransfer.files?.[0];
    if (file && effectiveSchoolId) {
      handleFileUpload(file);
    }
  }

  async function handleSend() {
    if (!content.trim() || groups.length === 0) return;
    setSending(true);
    setError(null);
    try {
      const response_options = getResponseOptions();
      const payload = {
        groups,
        content: content.trim(),
        response_options,
        document_id: attachedDoc?.id,
      };
      if (contextType === "school") {
        await api.sendSchoolMessage(contextId, payload);
      } else if (contextType === "sport" && (schoolId ?? effectiveSchoolId)) {
        await api.sendSportMessage(contextId, (schoolId ?? effectiveSchoolId)!, payload);
      } else if (contextType === "age_group" && (schoolId ?? effectiveSchoolId)) {
        await api.sendAgeGroupMessage(contextId, { ...payload, school_id: (schoolId ?? effectiveSchoolId)! });
      } else {
        await api.sendMessage({
          context_type: contextType,
          context_id: contextId,
          ...payload,
          // 163 -- team sub-group target + explicit category
          ...(contextType === "team" ? { subgroup, category: "team" as const } : {}),
        });
      }
      setSent(true);
      onSent?.();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to send message");
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center overflow-y-auto p-4 sm:items-start sm:justify-end"
        data-dialog="send-message"
        data-state="sent"
      >
        <Card className="w-full max-w-md mt-16 mr-2">
          <CardContent className="pt-6 flex flex-col items-center gap-4">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <p className="text-lg font-medium">Message sent!</p>
            <p className="text-sm text-muted-foreground" data-region="sent-count">
              Sent to {preview?.total ?? 0} recipient{(preview?.total ?? 0) !== 1 ? "s" : ""}
            </p>
            <Button onClick={onClose} data-action="close">Close</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center overflow-y-auto p-4 sm:items-start sm:justify-end"
      data-dialog="send-message"
      data-state={sending ? "sending" : error ? "error" : "drafting"}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <Card className={`w-full max-w-md max-h-[90vh] overflow-y-auto transition-colors sm:mt-16 sm:mr-2 ${dragging ? "ring-2 ring-primary ring-offset-2" : ""}`}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="h-4 w-4" />
              Send Message
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose} data-action="close">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground" data-region="context-label">{contextLabel}</p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {error && <p className="text-sm text-destructive" data-error>{error}</p>}

          {/* Recipient groups */}
          <div data-region="recipients">
            <label className="text-sm font-medium mb-2 block">Recipients</label>
            <div className="flex gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={groups.includes("parents")}
                  onChange={() => toggleGroup("parents")}
                  className="rounded"
                  data-action="toggle-group-parents"
                />
                <Users className="h-4 w-4 text-muted-foreground" />
                Parents
                {preview && !loadingPreview && (
                  <span className="text-muted-foreground" data-count="parents">({preview.parents})</span>
                )}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={groups.includes("coaches")}
                  onChange={() => toggleGroup("coaches")}
                  className="rounded"
                  data-action="toggle-group-coaches"
                />
                <Users className="h-4 w-4 text-muted-foreground" />
                Coaches
                {preview && !loadingPreview && (
                  <span className="text-muted-foreground" data-count="coaches">({preview.coaches})</span>
                )}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={groups.includes("players")}
                  onChange={() => toggleGroup("players")}
                  className="rounded"
                  data-action="toggle-group-players"
                />
                <Users className="h-4 w-4 text-muted-foreground" />
                Players
                {preview && !loadingPreview && (
                  <span className="text-muted-foreground" data-count="players">({preview.players})</span>
                )}
              </label>
            </div>
            {preview && !loadingPreview && (
              <p className="text-xs text-muted-foreground mt-1" data-region="recipient-total" data-recipient-count={preview.total}>
                Total: {preview.total} recipient{preview.total !== 1 ? "s" : ""}
              </p>
            )}
          </div>

          {/* 163 -- team sub-group target (forwards/backs by position) */}
          {contextType === "team" && (
            <div data-region="subgroup">
              <label className="text-sm font-medium mb-1 block">Target group</label>
              <div className="flex gap-1.5" role="group" aria-label="Sub-group">
                {([
                  { v: null, label: "Whole team" },
                  { v: "forwards" as const, label: "Forwards" },
                  { v: "backs" as const, label: "Backs" },
                ]).map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => setSubgroup(opt.v)}
                    data-action={`subgroup-${opt.v ?? "all"}`}
                    data-selected={subgroup === opt.v}
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      subgroup === opt.v
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Forwards = jerseys 1-8, Backs = 9-15 (from team positions).
              </p>
            </div>
          )}

          {/* Message content */}
          <div data-region="message-content">
            <label className="text-sm font-medium mb-1 block">Message</label>
            <textarea
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[100px] resize-y"
              placeholder="Type your message..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={1000}
              data-field="content"
            />
            <p className="text-xs text-muted-foreground text-right">
              {content.length}/1000
            </p>
          </div>

          {/* Response options */}
          <div data-region="response-options">
            <label className="text-sm font-medium mb-1 block">Response options</label>
            <select
              aria-label="Response preset"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={responsePreset}
              onChange={(e) => setResponsePreset(e.target.value)}
              data-field="response-preset"
            >
              {Object.entries(PRESET_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            {responsePreset !== "none" && responsePreset !== "custom" && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {RESPONSE_PRESETS[responsePreset as ResponsePresetKey].map((opt) => (
                  <span
                    key={opt.value}
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      opt.color === "green" ? "bg-green-100 text-green-800" :
                      opt.color === "red" ? "bg-red-100 text-red-800" :
                      opt.color === "yellow" ? "bg-yellow-100 text-yellow-800" :
                      "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {opt.label}
                  </span>
                ))}
              </div>
            )}
            {responsePreset === "custom" && (
              <div className="mt-2 flex flex-col gap-2">
                {customOptions.map((opt) => (
                  <div key={opt.id} className="flex gap-2 items-center">
                    <input
                      className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm"
                      placeholder="Label"
                      value={opt.label}
                      onChange={(e) => {
                        const label = e.target.value;
                        // Auto-generate value from label
                        const value = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
                        setCustomOptions((prev) =>
                          prev.map((o) => (o.id === opt.id ? { ...o, label, value } : o)),
                        );
                      }}
                    />
                    <select
                      aria-label="Option color"
                      className="rounded-md border border-input bg-background px-2 py-1 text-sm w-20"
                      value={opt.color ?? "green"}
                      onChange={(e) => {
                        const color = e.target.value;
                        setCustomOptions((prev) =>
                          prev.map((o) => (o.id === opt.id ? { ...o, color } : o)),
                        );
                      }}
                    >
                      <option value="green">Green</option>
                      <option value="red">Red</option>
                      <option value="yellow">Yellow</option>
                    </select>
                    {customOptions.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => setCustomOptions((prev) => prev.filter((o) => o.id !== opt.id))}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
                {customOptions.length < 10 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCustomOptions((prev) => [...prev, { id: crypto.randomUUID(), label: "", value: "", color: "green" }])}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Add option
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Document attachment */}
          {effectiveSchoolId && (
            <div>
              {uploading ? (
                <div className="flex items-center gap-2 rounded-md border border-input bg-muted/30 px-3 py-2 text-sm">
                  <Upload className="h-4 w-4 text-muted-foreground shrink-0 animate-pulse" />
                  <span className="text-muted-foreground">Uploading...</span>
                </div>
              ) : attachedDoc ? (
                <div className="flex items-center gap-2 rounded-md border border-input bg-muted/30 px-3 py-2 text-sm">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate flex-1">{attachedDoc.original_name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAttachedDoc(null)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenDocPicker}
                  className="w-full"
                >
                  <Paperclip className="mr-2 h-4 w-4" />
                  Attach Document
                </Button>
              )}
              {showDocPicker && !attachedDoc && !uploading && (
                <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-input divide-y divide-border">
                  {schoolDocs.length === 0 && (
                    <p className="px-3 py-2 text-sm text-muted-foreground">No documents available</p>
                  )}
                  {schoolDocs.map((doc) => (
                    <button
                      key={doc.id}
                      type="button"
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-muted text-left"
                      onClick={() => {
                        setAttachedDoc(doc);
                        setShowDocPicker(false);
                      }}
                    >
                      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate">{doc.original_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Drag-and-drop hint */}
          {dragging && effectiveSchoolId && (
            <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-primary bg-primary/5 py-4">
              <Upload className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium text-primary">Drop file to attach</p>
            </div>
          )}

          {/* Send button */}
          <Button
            onClick={handleSend}
            disabled={!content.trim() || groups.length === 0 || sending || uploading || (preview?.total ?? 0) === 0}
            data-action="send"
          >
            <Send className="mr-2 h-4 w-4" />
            {sending ? "Sending..." : `Send to ${preview?.total ?? 0} recipient${(preview?.total ?? 0) !== 1 ? "s" : ""}`}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
